import {UIGameManager} from '../UIGameManager'
import {SpriteView} from '../../ui_canvas_engine/SpriteView'
import {TextView} from '../../ui_canvas_engine/TextView'
import {GameFont} from '../../GameFont'
import {PIXI} from '@colonist/pixi'
import {SectionView} from '../../ui_canvas_engine/SectionView'
import {BaseCardData, SelectCardPopupForPendingDistributionFormat} from '@colonist/game-card-types'
import {PopupInformation} from '@colonist/game-base-types'
import {TranslationObject} from '@colonist/i18n'
import {UIUtilsEjsVariables} from '../../UIUtilsEjsVariables'
import {UIGameSpriteHandler} from '../../UIGameSpriteHandler'
import {CardDistributionType, TileCornerBuildingType, TileEdgeType} from '@colonist/game-states'
import {robberSkinController} from '../UIGameRobberSkinController'
import {CARD_IMAGES, CARD_POPUP_BODY, CARD_TIP_TITLE} from '@colonist/ui-game'
import {UIImagePathController} from '../../UIEnums.ts'
import TileGold from '@static/images/tile_gold.svg'
import IconAqueduct from '@static/images/icon_aqueduct.svg'
import IconBarbarianShip from '@static/images/icon_barbarian_ship.svg'

export class UIGamePopupInformationView extends SectionView {
    private readonly uiGameManager: UIGameManager
    private leftImage: SpriteView
    title: TextView
    body: TextView
    private readonly minimumH: number
    private readonly popupInformation: PopupInformation

    constructor(uiGameManager: UIGameManager, x: number, y: number, w: number, popupInformation: PopupInformation, container: PIXI.Container | undefined = undefined, overrideText: string | undefined = undefined) {
        const canvas = uiGameManager.canvasController

        const isUserOnMobileApp = (UIUtilsEjsVariables.isUserOnMobileApp && UIUtilsEjsVariables.shouldDisplayNewMobileGameUI)
        const minimumH = isUserOnMobileApp ? canvas.itemSize * 1.5 : canvas.itemSize * 1.25

        super(x, y, w, minimumH, container)
        this.uiGameManager = uiGameManager
        this.minimumH = minimumH
        this.popupInformation = popupInformation

        this.createLeftImage()
        this.createTitle()
        this.createBodyText(overrideText)

        if(!isUserOnMobileApp) this.leftImage.centerYInParent()
    }

    private createLeftImage() {
        const imageH = this.minimumH * 0.8
        const texture = UIGameSpriteHandler.getTextureFromSpritesheet(this.popupInformation.imagePath)
        const textureRatio = texture.height / texture.width
        const imageW = imageH / textureRatio
        const imageX = this.minimumH * 0.1
        const imageY = this.minimumH * 0.1
        this.leftImage = new SpriteView(imageX, imageY, imageW, imageH, this, texture)
    }

    private createTitle() {
        const titleText = this.popupInformation.title
        const titleX = this.leftImage.x + this.leftImage.width + this.minimumH * 0.1
        const titleW = this.width * 0.75
        const titleY = this.minimumH * 0.1
        const titleH = this.minimumH * 0.2
        const isUserOnMobileApp = (UIUtilsEjsVariables.isUserOnMobileApp && UIUtilsEjsVariables.shouldDisplayNewMobileGameUI)
        const fontSize = isUserOnMobileApp ? GameFont.Size.L : GameFont.Size.XL
        this.title = new TextView(titleText, titleX, titleY, titleW, titleH, this, fontSize, undefined, false)
        this.title.changeStyle({align: 'left'})
        this.title.makeSemiBold()
    }

    private createBodyText(overrideText: string | undefined) {
        const padding = this.minimumH * 0.05
        const bodyY = this.title.y + this.title.height + padding
        const bodyH = this.minimumH * 0.6
        const bodyW = this.width - this.leftImage.width - padding * 3
        const bodyText: TranslationObject = overrideText == undefined ? this.popupInformation.body : {key: 'strings:colonistCommon.utils.#buffer', options: {value: overrideText}}
        this.body = new TextView(bodyText, this.title.x, bodyY, bodyW, bodyH, this, GameFont.Size.Normal)
        this.body.repositionY(false)
        this.body.changeStyle({align: 'left'})
    }

    scaleImage(scale: number) {
        const imageWidth = this.leftImage.width
        const imageHeight = this.leftImage.height

        this.leftImage.width *= scale
        this.leftImage.height *= scale

        this.leftImage.x += (imageWidth - this.leftImage.width) / 2
        this.leftImage.y += (imageHeight - this.leftImage.height) / 2
    }

    doesTextExtendBelowImage(): boolean {
        const textBottom = this.body.y + this.body.height
        const cardBottom = this.leftImage.y + this.leftImage.height
        return textBottom > cardBottom
    }

    updateBody(text: TranslationObject) {
        this.body.setText(text)
        this.body.repositionY(false)
        this.body.changeStyle({align: 'left'})
    }

    static getPopupInformationForCardData(cardData: BaseCardData): PopupInformation {
        const newPopupInformation: PopupInformation = {
            imagePath: CARD_IMAGES[cardData.cardEnum],
            title: CARD_TIP_TITLE[cardData.cardEnum],
            body: CARD_POPUP_BODY[cardData.cardEnum],
        }
        return newPopupInformation
    }

    static getPopupInformationForPendingDistributions(pendingDistributionSelectFormat: SelectCardPopupForPendingDistributionFormat): PopupInformation | undefined {
        switch(pendingDistributionSelectFormat.selectionType) {
            case CardDistributionType.ResourceTile:
            case CardDistributionType.StartingResources: return undefined
            case CardDistributionType.GoldTile: return UIGamePopupInformationView.getPopupInformationForGoldTileDistribution(pendingDistributionSelectFormat)
            case CardDistributionType.Aqueduct: return UIGamePopupInformationView.getPopupInformationForAqueductDistribution(pendingDistributionSelectFormat)
            case CardDistributionType.BarbarianProgressCard: return UIGamePopupInformationView.getPopupInformationForBarbarianProgressCardDistribution(pendingDistributionSelectFormat)
        }
    }

    static getPopupInformationForGoldTileDistribution(pendingDistributionSelectFormat: SelectCardPopupForPendingDistributionFormat): PopupInformation {
        const amountToSelect = pendingDistributionSelectFormat.selectCardFormat.amountOfCardsToSelect

        const popupInformation: PopupInformation = {
            imagePath: TileGold,
            title: {key: 'strings:game.distribution.goldTile'},
            body: {
                key: 'strings:game.distribution.body',
                options: {
                    count: amountToSelect,
                },
            },
        }
        return popupInformation
    }

    static getPopupInformationForAqueductDistribution(pendingDistributionSelectFormat: SelectCardPopupForPendingDistributionFormat): PopupInformation {
        const amountToSelect = pendingDistributionSelectFormat.selectCardFormat.amountOfCardsToSelect

        const popupInformation: PopupInformation = {
            imagePath: IconAqueduct,
            title: {key: 'strings:game.distribution.aqueduct'},
            body: {
                key: 'strings:game.distribution.body',
                options: {
                    count: amountToSelect,
                },
            },
        }
        return popupInformation
    }

    static getPopupInformationForBarbarianProgressCardDistribution(pendingDistributionSelectFormat: SelectCardPopupForPendingDistributionFormat): PopupInformation {
        const popupInformation: PopupInformation = {
            imagePath: IconBarbarianShip,
            title: {key: 'strings:game.distribution.barbarianProgressCard.title'},
            body: {key: 'strings:game.distribution.barbarianProgressCard.body'},
        }
        return popupInformation
    }

    static getPopupInformationForPlayerRolledSeven(uiGameManager: UIGameManager): PopupInformation {
        const popupInformation: PopupInformation = {
            imagePath: robberSkinController.getRobberSkin(),
            title: {key: 'strings:game.tips.robber.title'},
            body: {key: 'strings:game.prompts.playerRolledSeven'},
        }
        return popupInformation
    }

    static getPopupInformationForInitialRoadPlacement(uiGameManager: UIGameManager): PopupInformation {
        const imagePath = UIImagePathController.roadsByColor[uiGameManager.gameController.myColor]

        const isFirstInitialRoad = uiGameManager.gameController.mapValidator.tileState.getPlacedEdgePieceCountForPlayer(TileEdgeType.Road, uiGameManager.gameController.myColor) == 0

        if(isFirstInitialRoad) {
            return {
                imagePath: imagePath,
                title: {key: 'strings:game.prompts.initialPlacements.firstRoad.title'},
                body: {key: 'strings:game.prompts.initialPlacements.firstRoad.body'},
            }
        }

        return {
            imagePath: imagePath,
            title: {key: 'strings:game.prompts.initialPlacements.secondRoad.title'},
            body: {key: 'strings:game.prompts.initialPlacements.secondRoad.body'},
        }
    }

    static getPopupInformationForInitialSettlementPlacement(uiGameManager: UIGameManager): PopupInformation {
        const imagePath = UIImagePathController.settlementsByColor[uiGameManager.gameController.myColor]

        const placedSettlementAmount = uiGameManager.gameController.mapValidator.tileState.getPlacedCornerBuildingCountForPlayer(TileCornerBuildingType.Settlement, uiGameManager.gameController.myColor)
        const isFirstInitialSettlement = placedSettlementAmount == 0

        if(isFirstInitialSettlement) {
            return {
                imagePath: imagePath,
                title: {key: 'strings:game.prompts.initialPlacements.firstSettlement.title'},
                body: {key: 'strings:game.prompts.initialPlacements.firstSettlement.body'},
            }
        }

        return {
            imagePath: imagePath,
            title: {key: 'strings:game.prompts.initialPlacements.secondSettlement.title'},
            body: {key: 'strings:game.prompts.initialPlacements.secondSettlement.body'},
        }
    }
}
